import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  BookOpen, Building2, Plus, Trash2, Download, Filter, Calendar, 
  ArrowUpCircle, ArrowDownCircle, Search, ChevronDown, ChevronUp,
  FileText, CheckCircle, Clock, AlertTriangle, X, Edit2, Check,
  Upload, FileSpreadsheet
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { API_URL } from '../../config/api';

const COMPANIES = [
  { number: '15429390', name: 'JUST RENOVATED LTD' },
  { number: '13801558', name: 'JUST CLEANED LIMITED' }
];

const INCOME_CATEGORIES = [
  { id: 'sales', name: 'Sales/Services' },
  { id: 'other_income', name: 'Other Income' },
  { id: 'refunds_received', name: 'Refunds Received' },
  { id: 'interest', name: 'Interest Received' },
];

const EXPENSE_CATEGORIES = [
  { id: 'materials', name: 'Materials & Supplies' },
  { id: 'subcontractors', name: 'Subcontractors' },
  { id: 'wages', name: 'Wages & Salaries' },
  { id: 'rent', name: 'Rent & Rates' },
  { id: 'utilities', name: 'Utilities (Gas, Electric, Water)' },
  { id: 'phone_internet', name: 'Phone & Internet' },
  { id: 'insurance', name: 'Insurance' },
  { id: 'vehicle', name: 'Vehicle Costs (Fuel, Repairs)' },
  { id: 'travel', name: 'Travel & Accommodation' },
  { id: 'office', name: 'Office Supplies' },
  { id: 'software', name: 'Software & Subscriptions' },
  { id: 'marketing', name: 'Marketing & Advertising' },
  { id: 'professional', name: 'Professional Fees (Accountant, Legal)' },
  { id: 'bank_charges', name: 'Bank Charges' },
  { id: 'equipment', name: 'Equipment & Tools' },
  { id: 'training', name: 'Training & Development' },
  { id: 'entertainment', name: 'Business Entertainment' },
  { id: 'other_expense', name: 'Other Expenses' },
];

const Bookkeeping = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('transactions');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const fileInputRef = useRef(null);
  
  // Filters - default to last 12 months
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear() - 1, new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // New transaction form
  const [newTxn, setNewTxn] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    type: 'income',
    category: 'sales',
    reference: '',
    notes: ''
  });
  
  // P&L Report
  const [plReport, setPlReport] = useState(null);
  const [showPLDetail, setShowPLDetail] = useState(false);

  useEffect(() => {
    if (selectedCompany) {
      loadTransactions();
    }
  }, [selectedCompany, dateRange]);

  const loadTransactions = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    
    try {
      const params = new URLSearchParams({
        company_number: selectedCompany.number,
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      const res = await fetch(`${API_URL}/api/admin/bookkeeping/transactions?${params}`);
      const data = await res.json();
      setTransactions(data.transactions || []);
      
      // Also load P&L report
      const plRes = await fetch(`${API_URL}/api/admin/bookkeeping/reports/profit-loss?company_number=${selectedCompany.number}&start_date=${dateRange.start}&end_date=${dateRange.end}`);
      const plData = await plRes.json();
      setPlReport(plData);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  // CSV Import handlers
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImportLoading(true);
    setImportError('');
    setImportPreview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('company_number', selectedCompany.number);
      
      const res = await fetch(`${API_URL}/api/admin/bookkeeping/import/preview`, {
        method: 'POST',
        body: formData
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to parse CSV');
      }
      
      if (data.success) {
        setImportPreview(data);
        setShowImportModal(true);
      } else {
        setImportError(data.error || 'Could not parse CSV file');
      }
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImportLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImportCategoryChange = (index, newCategory) => {
    setImportPreview(prev => ({
      ...prev,
      transactions: prev.transactions.map((txn, i) => 
        i === index ? { ...txn, category: newCategory } : txn
      )
    }));
  };

  const handleImportSkipToggle = (index) => {
    setImportPreview(prev => ({
      ...prev,
      transactions: prev.transactions.map((txn, i) => 
        i === index ? { ...txn, skip: !txn.skip } : txn
      )
    }));
  };

  const handleConfirmImport = async () => {
    if (!importPreview) return;
    
    setImportLoading(true);
    
    try {
      const transactionsToImport = importPreview.transactions.map(txn => ({
        ...txn,
        category: txn.category || txn.suggested_category
      }));
      
      const res = await fetch(`${API_URL}/api/admin/bookkeeping/import/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_number: selectedCompany.number,
          transactions: transactionsToImport
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Auto-adjust date range to include imported transactions
        const dates = transactionsToImport.filter(t => !t.skip).map(t => t.date);
        if (dates.length > 0) {
          const minDate = dates.reduce((a, b) => a < b ? a : b);
          const maxDate = dates.reduce((a, b) => a > b ? a : b);
          
          // Expand date range if needed
          setDateRange(prev => ({
            start: prev.start > minDate ? minDate : prev.start,
            end: prev.end < maxDate ? maxDate : prev.end
          }));
        }
        
        setShowImportModal(false);
        setImportPreview(null);
        loadTransactions();
        alert(`Successfully imported ${data.imported_count} transactions!`);
      } else {
        throw new Error(data.detail || 'Failed to import');
      }
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImportLoading(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!newTxn.description || !newTxn.amount) return;
    
    try {
      const payload = {
        company_number: selectedCompany.number,
        date: newTxn.date,
        description: newTxn.description,
        amount: newTxn.type === 'expense' ? -Math.abs(parseFloat(newTxn.amount)) : Math.abs(parseFloat(newTxn.amount)),
        type: newTxn.type,
        category: newTxn.category,
        reference: newTxn.reference,
        notes: newTxn.notes,
        reconciled: false
      };
      
      const res = await fetch(`${API_URL}/api/admin/bookkeeping/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        setShowAddForm(false);
        setNewTxn({
          date: new Date().toISOString().split('T')[0],
          description: '',
          amount: '',
          type: 'income',
          category: 'sales',
          reference: '',
          notes: ''
        });
        loadTransactions();
      }
    } catch (err) {
      console.error('Failed to add transaction:', err);
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/bookkeeping/transactions/${id}`, {
        method: 'DELETE'
      });
      loadTransactions();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleUpdateCategory = async (id, newCategory) => {
    try {
      await fetch(`${API_URL}/api/admin/bookkeeping/transactions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: newCategory })
      });
      loadTransactions();
      setEditingId(null);
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const downloadPLReport = () => {
    if (!selectedCompany) return;
    window.open(
      `${API_URL}/api/admin/bookkeeping/reports/profit-loss/pdf?company_number=${selectedCompany.number}&start_date=${dateRange.start}&end_date=${dateRange.end}`,
      '_blank'
    );
  };

  const filteredTransactions = useMemo(() => {
    let filtered = transactions;
    
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.type === filterType);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.description.toLowerCase().includes(query) ||
        t.reference?.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [transactions, filterType, searchQuery]);

  const getCategoryName = (categoryId, type) => {
    const categories = type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    return categories.find(c => c.id === categoryId)?.name || categoryId;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Summary calculations
  const summary = useMemo(() => {
    const income = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expenses = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Math.abs(t.amount), 0);
    return { income, expenses, net: income - expenses };
  }, [filteredTransactions]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="bookkeeping-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-purple-600" />
          Bookkeeping
        </h1>
        <p className="text-gray-500 mt-1">Track income, expenses, and generate reports</p>
      </div>

      {/* Company Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-gray-600" />
          Select Company
        </h2>
        
        <div className="grid gap-3 md:grid-cols-2">
          {COMPANIES.map((company) => (
            <button
              key={company.number}
              onClick={() => setSelectedCompany(company)}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                selectedCompany?.number === company.number
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium text-gray-900">{company.name}</div>
              <div className="text-sm text-gray-500">{company.number}</div>
            </button>
          ))}
        </div>
      </div>

      {selectedCompany && (
        <>
          {/* Date Range & Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="h-10 w-40"
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="h-10 w-40"
                />
                <button
                  onClick={() => setDateRange({ start: '2020-01-01', end: '2030-12-31' })}
                  className="text-sm text-purple-600 hover:underline px-2"
                >
                  Show All
                </button>
              </div>
              
              <div className="flex items-center gap-2 ml-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 w-48 pl-9"
                  />
                </div>
                
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="h-10 px-3 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="income">Income Only</option>
                  <option value="expense">Expenses Only</option>
                </select>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Total Income</div>
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.income)}</div>
                </div>
                <ArrowUpCircle className="w-10 h-10 text-green-200" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Total Expenses</div>
                  <div className="text-2xl font-bold text-red-600">{formatCurrency(summary.expenses)}</div>
                </div>
                <ArrowDownCircle className="w-10 h-10 text-red-200" />
              </div>
            </div>
            
            <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${summary.net >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Net Profit/Loss</div>
                  <div className={`text-2xl font-bold ${summary.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(summary.net)}
                  </div>
                </div>
                {summary.net >= 0 ? (
                  <CheckCircle className="w-10 h-10 text-green-200" />
                ) : (
                  <AlertTriangle className="w-10 h-10 text-red-200" />
                )}
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Transactions</div>
                  <div className="text-2xl font-bold text-gray-900">{filteredTransactions.length}</div>
                </div>
                <FileText className="w-10 h-10 text-gray-200" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Transaction
            </Button>
            
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importLoading}
                className="flex items-center gap-2 border-purple-200 text-purple-600 hover:bg-purple-50"
              >
                <Upload className="w-4 h-4" />
                {importLoading ? 'Processing...' : 'Import Bank Statement'}
              </Button>
            </div>
            
            <Button
              variant="outline"
              onClick={downloadPLReport}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Download P&L Report
            </Button>
          </div>
          
          {importError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {importError}
              <button onClick={() => setImportError('')} className="ml-2 underline">Dismiss</button>
            </div>
          )}

          {/* Add Transaction Form */}
          {showAddForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Add Transaction</h3>
                <button onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <Input
                    type="date"
                    value={newTxn.date}
                    onChange={(e) => setNewTxn(prev => ({ ...prev, date: e.target.value }))}
                    className="h-11"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={newTxn.type}
                    onChange={(e) => setNewTxn(prev => ({ 
                      ...prev, 
                      type: e.target.value,
                      category: e.target.value === 'income' ? 'sales' : 'materials'
                    }))}
                    className="w-full h-11 px-3 border border-gray-200 rounded-lg"
                  >
                    <option value="income">Money In (Income)</option>
                    <option value="expense">Money Out (Expense)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newTxn.category}
                    onChange={(e) => setNewTxn(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full h-11 px-3 border border-gray-200 rounded-lg"
                  >
                    {(newTxn.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (£)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={newTxn.amount}
                    onChange={(e) => setNewTxn(prev => ({ ...prev, amount: e.target.value }))}
                    className="h-11"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Input
                    type="text"
                    placeholder="What was this for?"
                    value={newTxn.description}
                    onChange={(e) => setNewTxn(prev => ({ ...prev, description: e.target.value }))}
                    className="h-11"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference (Optional)</label>
                  <Input
                    type="text"
                    placeholder="Invoice #, Check #"
                    value={newTxn.reference}
                    onChange={(e) => setNewTxn(prev => ({ ...prev, reference: e.target.value }))}
                    className="h-11"
                  />
                </div>
                
                <div className="flex items-end">
                  <Button
                    onClick={handleAddTransaction}
                    disabled={!newTxn.description || !newTxn.amount}
                    className={`h-11 w-full ${newTxn.type === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                  >
                    Add {newTxn.type === 'income' ? 'Income' : 'Expense'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Transactions List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Transactions</h3>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No transactions found. Add your first transaction above!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredTransactions.map((txn) => (
                  <div key={txn.id} className="p-4 hover:bg-gray-50 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      txn.type === 'income' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {txn.type === 'income' ? (
                        <ArrowUpCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <ArrowDownCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{txn.description}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <span>{formatDate(txn.date)}</span>
                        <span>•</span>
                        {editingId === txn.id ? (
                          <select
                            value={txn.category}
                            onChange={(e) => handleUpdateCategory(txn.id, e.target.value)}
                            className="text-sm border border-gray-200 rounded px-2 py-0.5"
                            autoFocus
                            onBlur={() => setEditingId(null)}
                          >
                            {(txn.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        ) : (
                          <button 
                            onClick={() => setEditingId(txn.id)}
                            className="hover:text-purple-600 flex items-center gap-1"
                          >
                            {getCategoryName(txn.category, txn.type)}
                            <Edit2 className="w-3 h-3" />
                          </button>
                        )}
                        {txn.reference && (
                          <>
                            <span>•</span>
                            <span className="text-gray-400">{txn.reference}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className={`text-lg font-semibold ${txn.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                      {txn.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(txn.amount))}
                    </div>
                    
                    <button
                      onClick={() => handleDeleteTransaction(txn.id)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* P&L Summary */}
          {plReport && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
              <button
                onClick={() => setShowPLDetail(!showPLDetail)}
                className="w-full flex items-center justify-between"
              >
                <h3 className="text-lg font-semibold text-gray-900">Profit & Loss Summary</h3>
                {showPLDetail ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showPLDetail && (
                <div className="mt-4 space-y-4">
                  {/* Income breakdown */}
                  <div>
                    <h4 className="font-medium text-green-700 mb-2">Income by Category</h4>
                    <div className="space-y-1">
                      {plReport.income.categories.map((cat, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-600">{getCategoryName(cat.name, 'income')}</span>
                          <span className="text-green-600">{formatCurrency(cat.total)}</span>
                        </div>
                      ))}
                      {plReport.income.categories.length === 0 && (
                        <div className="text-sm text-gray-400">No income recorded</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Expense breakdown */}
                  <div>
                    <h4 className="font-medium text-red-700 mb-2">Expenses by Category</h4>
                    <div className="space-y-1">
                      {plReport.expenses.categories.map((cat, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-600">{getCategoryName(cat.name, 'expense')}</span>
                          <span className="text-red-600">{formatCurrency(cat.total)}</span>
                        </div>
                      ))}
                      {plReport.expenses.categories.length === 0 && (
                        <div className="text-sm text-gray-400">No expenses recorded</div>
                      )}
                    </div>
                  </div>
                  
                  {/* Totals */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">Total Income</span>
                      <span className="text-green-600 font-medium">{formatCurrency(plReport.income.total)}</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">Total Expenses</span>
                      <span className="text-red-600 font-medium">({formatCurrency(plReport.expenses.total)})</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="font-bold text-lg">Net Profit/(Loss)</span>
                      <span className={`font-bold text-lg ${plReport.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(plReport.net_profit)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Help Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mt-6">
            <h3 className="font-semibold text-yellow-800 mb-2">💡 Quick Tips</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• <strong>Money In</strong> = payments you received from customers</li>
              <li>• <strong>Money Out</strong> = payments you made for bills, supplies, etc.</li>
              <li>• <strong>Import Bank Statement</strong> = upload a CSV from your bank to auto-add transactions</li>
              <li>• Click on a category to change it if you made a mistake</li>
              <li>• The P&L Report shows your profit (income minus expenses) for any period</li>
            </ul>
          </div>
        </>
      )}

      {/* CSV Import Modal */}
      {showImportModal && importPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FileSpreadsheet className="w-6 h-6 text-purple-600" />
                  Import Bank Statement
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  {importPreview.total_count} transactions found. Review and categorize before importing.
                </p>
              </div>
              <button 
                onClick={() => { setShowImportModal(false); setImportPreview(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {importPreview.transactions.map((txn, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border ${txn.skip ? 'bg-gray-50 border-gray-200 opacity-50' : txn.type === 'income' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                  >
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleImportSkipToggle(index)}
                        className={`w-6 h-6 rounded border flex items-center justify-center ${txn.skip ? 'bg-gray-200 border-gray-300' : 'bg-white border-gray-300'}`}
                      >
                        {!txn.skip && <Check className="w-4 h-4 text-green-600" />}
                      </button>
                      
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${txn.type === 'income' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {txn.type === 'income' ? (
                          <ArrowUpCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <ArrowDownCircle className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{txn.description}</div>
                        <div className="text-sm text-gray-500">{txn.date}</div>
                      </div>
                      
                      <select
                        value={txn.category || txn.suggested_category}
                        onChange={(e) => handleImportCategoryChange(index, e.target.value)}
                        disabled={txn.skip}
                        className="text-sm border border-gray-200 rounded px-2 py-1 w-48"
                      >
                        {(txn.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                      
                      <div className={`text-lg font-semibold w-28 text-right ${txn.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                        {txn.type === 'income' ? '+' : '-'}£{txn.amount.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Importing: {importPreview.transactions.filter(t => !t.skip).length} of {importPreview.total_count} transactions
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowImportModal(false); setImportPreview(null); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={importLoading || importPreview.transactions.filter(t => !t.skip).length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {importLoading ? 'Importing...' : `Import ${importPreview.transactions.filter(t => !t.skip).length} Transactions`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookkeeping;
